import { useEffect, useState } from "react";
import React from "react";
// material-ui
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Formik } from "formik";
import {
  Button,
  FormControl,
  DialogTitle,
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Backdrop,
  CircularProgress,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";

// third-party
import _ from "lodash";
import * as Yup from "yup";

// project imports
import { openSnackbar } from "api/snackbar";
import { FormattedMessage } from "react-intl";
// assets

// types
import MainCard from "components/MainCard";
import { USERAXIOS } from "../../../../api/userAxios";
import { useNavigate, useParams } from "react-router";
import ImageUploader from "components/ImageUploader";
import { Vacancy } from "types/vacancy";
import { mockVacancyData } from "./mockData";

// constant
// ==============================|| PAGE ADD / EDIT - FORM ||============================== //

const AddEditPage = () => {
  const { pageId } = useParams();
  const [fetchingData, setFetchingData] = useState<boolean>(false);
  const [customer, setcustomer] = useState<any>();
  const [isMilitaryServiceDisabled, setIsMilitaryServiceDisabled] =
    useState(false);

  const navigate = useNavigate();
  const [vacancyData, setvacancyData] = useState<Vacancy>(mockVacancyData);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    // Check if a file was selected
    if (file) {
      // Check if the selected file is an image
      if (file.type.startsWith("image/")) {
        setvacancyData({ ...vacancyData, page_photo: file });
        // Create preview URL
        const fileUrl = URL.createObjectURL(file);
        setPreviewUrl(fileUrl);
      } else {
        setvacancyData({ ...vacancyData, page_photo: null });
        setPreviewUrl(null);
      }
    }
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleChange = (event: React.ChangeEvent<any>) => {
    const { name, value, type, checked } = event.target;

    // Handle different input types
    const newValue = type === "checkbox" ? checked : value;

    // Update profileData state with the new value
    setvacancyData((prevProfileData) => ({
      ...prevProfileData,
      [name]: newValue,
    }));

    // Toggle military service field based on selected gender
    if (name === "gender" && value === "FEMALE") {
      setIsMilitaryServiceDisabled(true);
    } else if (name === "gender" && value === "MALE") {
      setIsMilitaryServiceDisabled(false);
    }
  };

  // Add a specific handler for ReactQuill
  const handleQuillChange = (content: string) => {
    setvacancyData((prevData) => ({
      ...prevData,
      body: content,
    }));
  };

  useEffect(() => {
    const fetchPage = async () => {
      if (pageId) {
        setFetchingData(true);
        try {
          const response = await USERAXIOS.get(`/pages/${pageId}`);
          const data = response.data.data;

          setvacancyData({
            ...data,
            status: data?.status === "published",
          });
          setvacancyData(data);
          setcustomer(data);
          setPreviewUrl(data?.page_photo);
        } catch (error) {
          openSnackbar({
            open: true,
            message: "Failed to fetch page data",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: { color: "error" },
          });
        } finally {
          setFetchingData(false);
        }
      }
    };

    fetchPage();
  }, [pageId]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    short_text: Yup.string().required("Short Text is required"),
  });

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "primary",
        }}
        open={fetchingData}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
        </Stack>
      </Backdrop>

      <Formik
        initialValues={{
          title: vacancyData?.title,
          short_text: vacancyData?.short_text || "",
          body: vacancyData?.body || "",
          page_photo: vacancyData?.page_photo,
          // status: vacancyData?.status || false,
        }}
        validationSchema={validationSchema} // Pass the validation schema to Formik
        onSubmit={async (values, { setSubmitting }) => {
          // Prepare FormData object
          const formData = new FormData();

          formData.append("title", vacancyData?.title);
          formData.append("user_id", "3");
          formData.append("short_text", vacancyData?.short_text);
          formData.append("body", vacancyData?.body); // Use vacancyData.body directly

          if (vacancyData?.page_photo && previewUrl?.startsWith("blob:"))
            formData.append("page_photo", vacancyData?.page_photo);
          formData.append("lang", vacancyData?.lang);
          formData.append("status", vacancyData?.status);

          try {
            if (!customer) {
              await USERAXIOS.post(`/pages`, formData);
              openSnackbar({
                open: true,
                message: "Page added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              });
            } else {
              await USERAXIOS.post(`/pages/${pageId}`, formData);
              openSnackbar({
                open: true,
                message: "Page updated successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              });
            }
            navigate(-1);
          } catch (error) {
            openSnackbar({
              open: true,
              message: error.message || "Failed to submit page",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          touched,
          values,
        }) => (
          <form
            className="form-vacancy-container"
            noValidate
            onSubmit={handleSubmit}
          >
            <LocalizationProvider>
              <MainCard>
                <Grid item xs={12} md={12}>
                  <DialogTitle
                    color="primary"
                    style={{
                      textAlign: "center",
                      fontSize: "22px",
                      fontWeight: "bold",
                      marginBottom: "10px",
                    }}
                  >
                    {customer ? (
                      <FormattedMessage id="Edit Page" />
                    ) : (
                      <FormattedMessage id="New Page" />
                    )}
                  </DialogTitle>
                  {/* <Button
                    onClick={() => {
                      console.log("vacancyData", vacancyData);
                    }}
                  >
                    {" "}
                    vacancyData{" "}
                  </Button> */}
                  <Divider style={{ marginBottom: "20px" }} />
                  <Stack spacing={1}></Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="company_description">
                          <FormattedMessage id="Title" />
                        </InputLabel>
                        <TextField
                          fullWidth
                          value={vacancyData?.title}
                          name="title" // Add the name attribute
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ maxLength: 10 }}
                          placeholder="Title"
                          error={touched?.title && Boolean(errors?.title)}
                          helperText={touched?.title && errors?.title}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customr-package_validity">
                          <FormattedMessage id="Short Text" />
                        </InputLabel>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={vacancyData?.short_text}
                          name="short_text" // Add the name attribute
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Short Text"
                          error={
                            touched?.short_text && Boolean(errors?.short_text)
                          }
                          helperText={touched?.short_text && errors?.short_text}
                        />
                      </Stack>
                    </Grid>
                    <Grid style={{ height: "250px" }} item xs={12} sm={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="body">
                          <FormattedMessage id="Body" />
                        </InputLabel>
                        <ReactQuill
                          style={{ height: "150px" }}
                          theme="snow"
                          value={vacancyData?.body || ""}
                          onChange={handleQuillChange}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <FormattedMessage id="Page Image" />
                      </Typography>
                      <ImageUploader
                        id="page-image-upload"
                        previewUrl={previewUrl}
                        onChange={handleFileChange}
                        setFieldValue={setFieldValue}
                        label="Click to upload page image"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="lang">
                          <FormattedMessage id="Language" />
                        </InputLabel>
                        <FormControl fullWidth>
                          <Select
                            value={vacancyData?.lang}
                            onChange={(e: any) => handleChange(e)}
                            onBlur={handleBlur}
                            name="lang"
                          >
                            <MenuItem value="EN">
                              <FormattedMessage id="English" />
                            </MenuItem>
                            <MenuItem value="AR">
                              <FormattedMessage id="Arabic" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} marginTop="1.5rem">
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={vacancyData?.status === "published"}
                              onChange={(e) => {
                                setvacancyData({
                                  ...vacancyData,
                                  status: e.target.checked
                                    ? "published"
                                    : "unpublished",
                                });
                              }}
                              name="status"
                            />
                          }
                          label={<FormattedMessage id="Status" />}
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    <FormattedMessage id="Save" />{" "}
                  </Button> */}

                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    marginTop="4rem"
                  >
                    <Grid container justifyContent="center" alignItems="center">
                      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Grid item md={2} />
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate("/articles-list")}
                          >
                            <FormattedMessage id="Cancel" />
                          </Button>
                        </Grid>
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <CircularProgress
                                size="1.5rem"
                                style={{ color: "white" }}
                              />
                            ) : (
                              <FormattedMessage
                                id={pageId ? "Update" : "Create"}
                              />
                            )}
                          </Button>
                        </Grid>
                        <Grid item md={2} />
                      </Stack>
                    </Grid>
                  </Stack>
                </Grid>
              </MainCard>
              <Divider />
            </LocalizationProvider>
          </form>
        )}
      </Formik>
    </>
  );
};
export default AddEditPage;
